/**
 * AWS Icons Registry
 *
 * Provides official AWS service icons from aws-react-icons package.
 * Uses explicit DEFAULT imports for optimal tree-shaking.
 *
 * @see https://github.com/MKAbuMattar/aws-react-icons
 */

// Explicit DEFAULT imports for tree-shaking (aws-react-icons uses default exports)
import ArchitectureGroupAWSCloud from 'aws-react-icons/lib/icons/ArchitectureGroupAWSCloud';
import ArchitectureServiceAmazonAPIGateway from 'aws-react-icons/lib/icons/ArchitectureServiceAmazonAPIGateway';
import ArchitectureServiceAmazonCloudFront from 'aws-react-icons/lib/icons/ArchitectureServiceAmazonCloudFront';
import ArchitectureServiceAmazonCognito from 'aws-react-icons/lib/icons/ArchitectureServiceAmazonCognito';
import ArchitectureServiceAmazonDynamoDB from 'aws-react-icons/lib/icons/ArchitectureServiceAmazonDynamoDB';
import ArchitectureServiceAmazonEC2 from 'aws-react-icons/lib/icons/ArchitectureServiceAmazonEC2';
import ArchitectureServiceAmazonElasticContainerService from 'aws-react-icons/lib/icons/ArchitectureServiceAmazonElasticContainerService';
import ArchitectureServiceAmazonElasticKubernetesService from 'aws-react-icons/lib/icons/ArchitectureServiceAmazonElasticKubernetesService';
import ArchitectureServiceAmazonGuardDuty from 'aws-react-icons/lib/icons/ArchitectureServiceAmazonGuardDuty';
import ArchitectureServiceAmazonRDS from 'aws-react-icons/lib/icons/ArchitectureServiceAmazonRDS';
import ArchitectureServiceAmazonRoute53 from 'aws-react-icons/lib/icons/ArchitectureServiceAmazonRoute53';
import ArchitectureServiceAmazonSageMaker from 'aws-react-icons/lib/icons/ArchitectureServiceAmazonSageMaker';
import ArchitectureServiceAmazonSimpleStorageService from 'aws-react-icons/lib/icons/ArchitectureServiceAmazonSimpleStorageService';
import ArchitectureServiceAmazonVirtualPrivateCloud from 'aws-react-icons/lib/icons/ArchitectureServiceAmazonVirtualPrivateCloud';
import ArchitectureServiceAWSCloudFormation from 'aws-react-icons/lib/icons/ArchitectureServiceAWSCloudFormation';
import ArchitectureServiceAWSKeyManagementService from 'aws-react-icons/lib/icons/ArchitectureServiceAWSKeyManagementService';
import ArchitectureServiceAWSLambda from 'aws-react-icons/lib/icons/ArchitectureServiceAWSLambda';
import ArchitectureServiceAWSSecretsManager from 'aws-react-icons/lib/icons/ArchitectureServiceAWSSecretsManager';
import ArchitectureServiceAWSWAF from 'aws-react-icons/lib/icons/ArchitectureServiceAWSWAF';
import type { ComponentType, ReactNode } from 'react';

// Custom SVG imports (override package icons with custom designs)
import AwsIamKey from '@/sections/attacks/attackPaths/components/icons/bundles/aws/aws-iam-key.svg?react';
import type {
  IconDefinition,
  IconRenderer,
  IconRenderProps,
} from '@/sections/attacks/attackPaths/components/icons/registry/icon-renderers-map';
import { iconRenderers } from '@/sections/attacks/attackPaths/components/icons/registry/icon-renderers-map';
import { AWS_ICONS } from '@/sections/attacks/attackPaths/components/icons/types';

// ============================================================================
// Icon Component Type
// ============================================================================

type AwsIconComponent = ComponentType<{
  className?: string;
  width?: string | number;
  height?: string | number;
}>;

// ============================================================================
// Fallback Component (extracted to avoid recreating on each getAll() call)
// ============================================================================

const FallbackIcon = ({ className }: { className?: string }) => (
  <span className={className}>?</span>
);

// ============================================================================
// Icon Component Map
// ============================================================================

/**
 * Explicit mapping from AWS_ICONS names to aws-react-icons components.
 * Uses type-safe keys derived from AWS_ICONS const array.
 */
const awsIconComponents: Partial<
  Record<(typeof AWS_ICONS)[number], AwsIconComponent>
> = {
  Lambda: ArchitectureServiceAWSLambda,
  S3: ArchitectureServiceAmazonSimpleStorageService,
  EC2: ArchitectureServiceAmazonEC2,
  ECS: ArchitectureServiceAmazonElasticContainerService,
  EKS: ArchitectureServiceAmazonElasticKubernetesService,
  RDS: ArchitectureServiceAmazonRDS,
  DynamoDB: ArchitectureServiceAmazonDynamoDB,
  IAM: AwsIamKey,
  Cognito: ArchitectureServiceAmazonCognito,
  ApiGateway: ArchitectureServiceAmazonAPIGateway,
  CloudFormation: ArchitectureServiceAWSCloudFormation,
  SageMaker: ArchitectureServiceAmazonSageMaker,
  VPC: ArchitectureServiceAmazonVirtualPrivateCloud,
  CloudFront: ArchitectureServiceAmazonCloudFront,
  Route53: ArchitectureServiceAmazonRoute53,
  SecretsManager: ArchitectureServiceAWSSecretsManager,
  KMS: ArchitectureServiceAWSKeyManagementService,
  GuardDuty: ArchitectureServiceAmazonGuardDuty,
  WAF: ArchitectureServiceAWSWAF,
  Generic: ArchitectureGroupAWSCloud,
};

/**
 * Human-readable labels for AWS icons.
 */
const awsIconLabels: Partial<Record<(typeof AWS_ICONS)[number], string>> = {
  Lambda: 'AWS Lambda',
  S3: 'AWS S3',
  EC2: 'AWS EC2',
  ECS: 'AWS ECS',
  EKS: 'AWS EKS',
  RDS: 'AWS RDS',
  DynamoDB: 'AWS DynamoDB',
  IAM: 'AWS IAM',
  Cognito: 'AWS Cognito',
  ApiGateway: 'AWS API Gateway',
  CloudFormation: 'AWS CloudFormation',
  SageMaker: 'AWS SageMaker',
  VPC: 'AWS VPC',
  CloudFront: 'AWS CloudFront',
  Route53: 'AWS Route 53',
  SecretsManager: 'AWS Secrets Manager',
  KMS: 'AWS KMS',
  GuardDuty: 'AWS GuardDuty',
  WAF: 'AWS WAF',
  Generic: 'AWS (Generic)',
};

// ============================================================================
// AWS Renderer Implementation
// ============================================================================

/**
 * AWS icon renderer for the attack graph icon system.
 * Implements IconRenderer interface for registration with iconRenderers Map.
 */
const awsRenderer: IconRenderer = {
  render: (
    name: string,
    { size = 24, className }: IconRenderProps
  ): ReactNode => {
    const IconComponent = awsIconComponents[name as (typeof AWS_ICONS)[number]];
    if (!IconComponent) return null;
    return <IconComponent className={className} width={size} height={size} />;
  },

  validate: (name: string): boolean => {
    return (AWS_ICONS as readonly string[]).includes(name);
  },

  getIcon: (name: string) => {
    const IconComponent = awsIconComponents[name as (typeof AWS_ICONS)[number]];
    if (!IconComponent) return null;

    return function WrappedAwsIcon({
      size = 24,
      className,
    }: {
      size?: number;
      className?: string;
    }) {
      return <IconComponent className={className} width={size} height={size} />;
    };
  },

  getAll: (): IconDefinition[] =>
    AWS_ICONS.map(name => {
      const IconComponent = awsIconComponents[name];
      return {
        id: `aws:${name}`,
        component: IconComponent ?? FallbackIcon,
        label: awsIconLabels[name] ?? name,
        category: 'aws',
      };
    }),
};

// ============================================================================
// Register Renderer
// ============================================================================

iconRenderers.set('aws', awsRenderer);

export { awsRenderer };
